import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import JSZip from "jszip";
import { showSuccessAlert, showErrorAlert, showDeleteConfirmationAlert } from "../../utils/alertUtils";
import { formatCustomDateTime } from "../../utils/dateUtils";
import { AuthContext } from "../../context/authcontext";
import { useTabVisibility } from "../../hooks/useTabVisibility";
import { Button } from "react-bootstrap";
import axios from "axios";
import { API_BASE_URL } from "../../environment";
import { getAuthHeaders } from "../../utils/authUtils";
import AttachmentComponent, { Attachment as AttachmentType } from "../AttachmentComponent";

interface Attachment extends AttachmentType {
  attachment_id: number;
  title: string;
  size_in_kb?: number | null;
  related_entity_name: string;
  related_entity_id: number;
  attachment_url: string;
  created_datetime: string;
  tenant_id: number;
  created_by: number;
}

interface AttachmentsProps {
  tenant_id?: number | string | null;
  related_entity_id?: number | string | null;
  related_entity_name?: string;
  refreshTrigger?: number;
  onActivityUpdate?: () => void;
}

const Attachments: React.FC<AttachmentsProps> = ({
  tenant_id,
  related_entity_id,
  related_entity_name,
  refreshTrigger,
  onActivityUpdate,
}) => {
  const { auth } = useContext(AuthContext);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [downloadingAttachment, setDownloadingAttachment] = useState<number | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const currentTenantId = Number(tenant_id || localStorage.getItem("selectedTenantId"));
  const currentRelatedEntityId = Number(related_entity_id);

  const { hasBeenActivated } = useTabVisibility({
    tabId: 'attachments',
    onTabActivated: () => {
      if (!dataLoaded && currentTenantId && currentRelatedEntityId) {
        loadAttachments();
        setDataLoaded(true);
      }
    }
  });

  useEffect(() => {
    if (hasBeenActivated && !dataLoaded && currentTenantId && currentRelatedEntityId) {
      loadAttachments();
      setDataLoaded(true);
    }
  }, [hasBeenActivated, dataLoaded, currentTenantId, currentRelatedEntityId]);

  useEffect(() => {
    if (dataLoaded && refreshTrigger && currentTenantId && currentRelatedEntityId) {
      loadAttachments();
    }
  }, [refreshTrigger, dataLoaded, currentTenantId, currentRelatedEntityId]);

  const loadAttachments = async () => {
    if (!currentRelatedEntityId || !currentTenantId) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/attachment/`,
        { 
          headers: getAuthHeaders(),
          params: { 
            tenant_id: currentTenantId,
            related_entity_name: related_entity_name,
            related_entity_id: currentRelatedEntityId
          }
        }
      );
      
      if (response.data && Array.isArray(response.data)) {
        setAttachments(response.data);
      } else {
        setAttachments([]);
      }
    } catch (error) {
      console.error("Error loading attachments:", error);
      showErrorAlert("Failed to load attachments");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelection = (files: FileList | null) => {
    if (!files) return;

    const maxFileSize = 10 * 1024 * 1024; 
    const maxFiles = 5;
    const allowedTypes = [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx"];
    
    
    const currentFileCount = selectedFiles.length;
    const existingAttachmentsCount = attachments.length;
    const totalCurrentFiles = currentFileCount + existingAttachmentsCount;
    
    if (totalCurrentFiles + files.length > maxFiles) {
      const remainingSlots = maxFiles - totalCurrentFiles;
      if (remainingSlots <= 0) {
        showErrorAlert(`Maximum file limit reached! You can only upload up to ${maxFiles} files. Please remove some existing files before adding new ones.`);
        return;
      } else {
        showErrorAlert(`You can only add ${remainingSlots} more file(s). You currently have ${totalCurrentFiles} files.`);
        return;
      }
    }

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      
      if (file.size > maxFileSize) {
        errors.push(`${file.name} is too large. Maximum size is 10MB.`);
      } else if (!allowedTypes.includes(fileExtension)) {
        errors.push(`${file.name} is not allowed. Only ${allowedTypes.join(", ")} files are permitted.`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      showErrorAlert(errors.join(", "));
      return;
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadFiles = async () => {
    if (!currentRelatedEntityId || !currentTenantId || selectedFiles.length === 0) {
      showErrorAlert("Please select files to upload");
      return;
    }

    try {
      setUploadingFiles(true);
      
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('attachment_url', file);
      });
      
      
      formData.append('tenant_id', currentTenantId.toString());
      formData.append('related_entity_name', related_entity_name || 'proposal');
      formData.append('related_entity_id', currentRelatedEntityId.toString());
      formData.append('created_by', auth?.user_id?.toString() || '');

      await axios.post(
        `${API_BASE_URL}/attachment/`,
        formData,
        { 
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      setSelectedFiles([]);
      setShowAddFileModal(false);
      loadAttachments();
      showSuccessAlert("Files uploaded successfully");

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      showErrorAlert("Failed to upload files");
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      const result = await showDeleteConfirmationAlert(
        "Are you sure?",
        "This will permanently delete this attachment.",
        "Yes, delete it!",
        "Cancel"
      );

      if (result.isConfirmed) {
        
        await axios.delete(
          `${API_BASE_URL}/attachment/${attachmentId}/`,
          { 
            headers: getAuthHeaders(),
            params: { 
              tenant_id: currentTenantId
            }
          }
        );

        loadAttachments();
        showSuccessAlert("Attachment deleted successfully");

        if (onActivityUpdate) {
          onActivityUpdate();
        }
      }
    } catch (error) {
      console.error("Error deleting attachment:", error);
      showErrorAlert("Failed to delete attachment");
    }
  };

  const downloadAttachment = async (url: string, filename: string, attachmentId: number) => {
    try {
      setDownloadingAttachment(attachmentId);
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(url, "_blank");
    } finally {
      setDownloadingAttachment(null);
    }
  };

  const downloadAllAttachmentsAsZip = async () => {
    try {
      if (attachments.length === 0) {
        showErrorAlert("No attachments to download");
        return;
      }

      const zip = new JSZip();

      const downloadPromises = attachments.map(async (attachment) => {
        try {
          const response = await fetch(attachment.attachment_url);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${attachment.title}`);
          }
          const blob = await response.blob();

          let fileName = attachment.title;
          let counter = 1;
          while (zip.file(fileName)) {
            const parts = attachment.title.split(".");
            if (parts.length > 1) {
              const extension = parts.pop();
              const nameWithoutExt = parts.join(".");
              fileName = `${nameWithoutExt}_${counter}.${extension}`;
            } else {
              fileName = `${attachment.title}_${counter}`;
            }
            counter++;
          }

          zip.file(fileName, blob);
        } catch (error) {
          console.error(`Failed to download ${attachment.title}:`, error);
        }
      });

      await Promise.all(downloadPromises);

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const downloadUrl = window.URL.createObjectURL(zipBlob);
      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.download = `attachments.zip`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      showSuccessAlert("Download completed successfully");
    } catch (error) {
      console.error("Error creating zip file:", error);
      showErrorAlert("Failed to download attachments");
    }
  };

  const getAttachmentIcon = (filename: string | null | undefined) => {
    if (!filename) return "assets/img/icons/file.svg";
    const extension = filename.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "assets/img/icons/pdf.svg";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "assets/img/icons/image.svg";
      case "doc":
      case "docx":
        return "assets/img/icons/word.png";
      case "xls":
      case "xlsx":
        return "assets/img/icons/xls.svg";
      default:
        return "assets/img/icons/file.svg";
    }
  };

  const truncateFileName = (filename: string, maxLength: number) => {
    if (!filename || filename.length <= maxLength) return filename;

    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1) {
      return `${filename.substring(0, maxLength - 3)}...`;
    }

    const extension = filename.substring(lastDotIndex);
    const nameWithoutExt = filename.substring(0, lastDotIndex);

    if (extension.length >= maxLength - 3) {
      return `${filename.substring(0, maxLength - 3)}...`;
    }

    const availableLength = maxLength - extension.length - 3;

    if (nameWithoutExt.length <= availableLength) {
      return filename;
    }

    return `${nameWithoutExt.substring(0, availableLength)}...${extension}`;
  };

  return (
    <>
      <div className="tab-pane fade" id="attachments">
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="fw-semibold">Attachments</h4>
                             <div className="d-flex gap-2 align-items-center">
                 <small className={(() => {
                   if (attachments.length >= 5) return "text-danger fw-bold";
                   if (attachments.length >= 4) return "text-warning fw-bold";
                   return "text-muted";
                 })()}>
                   {attachments.length}/5 files
                 </small>
                 <Button 
                   className="btn btn-primary" 
                   onClick={() => setShowAddFileModal(true)}
                   disabled={attachments.length >= 5}
                   title={attachments.length >= 5 ? "Maximum file limit reached (5 files)" : "Add attachment"}
                 >
                   <i className="ti ti-circle-plus me-1" />
                   Add Attachment
                 </Button>
               </div>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : attachments.length > 0 ? (
              <div className="row">
                {attachments.map((attachment) => (
                  <div key={attachment.attachment_id} className="col-md-4 col-lg-4 mb-3">
                    <div className="upload-file upload-list">
                      <div className="d-flex align-items-center">
                        <ImageWithBasePath
                          src={getAttachmentIcon(attachment.title)}
                          alt="file"
                          className="me-2"
                        />
                        <div className="flex-grow-1 me-2">
                          <h6 title={attachment.title} style={{cursor:'default'}}>
                            {truncateFileName(attachment.title, 20)}
                          </h6>
                          <p className="mb-0">
                            {attachment.size_in_kb ? `${attachment.size_in_kb.toFixed(2)} KB` : 'Unknown size'}
                          </p>
                          <small className="text-muted">
                            {formatCustomDateTime(attachment.created_datetime)}
                          </small>
                        </div>
                        <div className="file-action d-flex align-items-end gap-1 flex-shrink-0">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => downloadAttachment(attachment.attachment_url, attachment.title, attachment.attachment_id)}
                            disabled={downloadingAttachment === attachment.attachment_id}
                            title="Download"
                          >
                            {downloadingAttachment === attachment.attachment_id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <i className="ti ti-arrow-down" />
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteAttachment(attachment.attachment_id)}
                            title="Delete"
                          >
                            <i className="ti ti-trash-x" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center p-4">
                <p className="text-muted">No attachments found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Files Modal */}
      {showAddFileModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Upload Files</h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddFileModal(false);
                    setSelectedFiles([]);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Select Files</label>
                  <div className="drag-attach">
                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => handleFileSelection(e.target.files)}
                      disabled={(() => {
                        const currentFileCount = selectedFiles.length;
                        const existingAttachmentsCount = attachments.length;
                        const totalCurrentFiles = currentFileCount + existingAttachmentsCount;
                        return totalCurrentFiles >= 5;
                      })()}
                    />
                    <div className="img-upload">
                      <i className="ti ti-file-broken" />
                      Upload Files (JPG, PNG, PDF, DOC, XLS)
                      <br />
                      <small className="text-muted">
                        Max 10MB per file, up to 5 files total
                      </small>
                    </div>
                  </div>
                  <div className="mt-2 d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Current files: {(() => {
                        const currentFileCount = selectedFiles.length;
                        const existingAttachmentsCount = attachments.length;
                        const totalCurrentFiles = currentFileCount + existingAttachmentsCount;
                        return `${totalCurrentFiles}/5`;
                      })()}
                    </small>
                    {(() => {
                      const currentFileCount = selectedFiles.length;
                      const existingAttachmentsCount = attachments.length;
                      const totalCurrentFiles = currentFileCount + existingAttachmentsCount;
                      if (totalCurrentFiles >= 5) {
                        return <small className="text-danger fw-bold">File limit reached</small>;
                      } else if (totalCurrentFiles >= 4) {
                        return <small className="text-warning fw-bold">Almost at limit</small>;
                      }
                      return null;
                    })()}
                  </div>
                </div>

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Selected Files</label>
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="upload-file upload-list">
                        <div className="d-flex align-items-center">
                          <ImageWithBasePath
                            src={getAttachmentIcon(file.name)}
                            alt="file"
                            className="me-2"
                          />
                          <div>
                            <h6 title={file.name}>
                              {truncateFileName(file.name, 25)}
                            </h6>
                            <p className="mb-0">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Link
                          to="#"
                          className="text-danger"
                          onClick={() => removeSelectedFile(index)}
                        >
                          <i className="ti ti-trash-x" />
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={() => {
                    setShowAddFileModal(false);
                    setSelectedFiles([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={uploadingFiles || selectedFiles.length === 0 || (() => {
                    const currentFileCount = selectedFiles.length;
                    const existingAttachmentsCount = attachments.length;
                    const totalCurrentFiles = currentFileCount + existingAttachmentsCount;
                    return totalCurrentFiles > 5;
                  })()}
                  onClick={handleUploadFiles}
                  title={(() => {
                    const currentFileCount = selectedFiles.length;
                    const existingAttachmentsCount = attachments.length;
                    const totalCurrentFiles = currentFileCount + existingAttachmentsCount;
                    if (totalCurrentFiles > 5) return "File limit exceeded";
                    return "Upload files";
                  })()}
                >
                  {uploadingFiles ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Uploading...
                    </>
                  ) : (
                    "Upload Files"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Attachments;
